import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { STATUS_LABELS } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid,
} from "recharts";
import ZoneHeatmap from "@/components/ZoneHeatmap";
import { ClipboardList, Clock, CheckCircle2, AlertTriangle, TrendingUp, Layers, Map as MapIcon } from "lucide-react";

const COLORS = ["hsl(221 83% 53%)", "hsl(173 58% 39%)", "hsl(27 87% 67%)", "hsl(340 75% 55%)", "hsl(43 74% 66%)"];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <Layout><div className="p-10">Loading...</div></Layout>;
  if (data === false) return <Layout><div className="p-10">Failed to load dashboard</div></Layout>;

  const statusData = (data.by_status || []).map((s) => ({ name: STATUS_LABELS[s.status] || s.status, value: s.count }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 space-y-8">
        <div>
          <div className="overline text-slate-500 mb-1">{ROLE_LABELS[user?.role] || "Dashboard"}</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Noise Pollution Command Center</h1>
          <p className="text-slate-600 mt-1 text-sm">Live view of complaints across Nagpur zones · Auto-refreshes on navigation.</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="kpi-grid">
          <KPI icon={ClipboardList} label="Total complaints" value={data.total} tone="slate" testid="kpi-total" />
          <KPI icon={Clock} label="Pending" value={data.pending} tone="amber" testid="kpi-pending" />
          <KPI icon={CheckCircle2} label="Resolved" value={data.resolved} tone="emerald" testid="kpi-resolved" />
          <KPI icon={AlertTriangle} label="Escalated" value={data.escalated} tone="red" testid="kpi-escalated" />
          <KPI icon={TrendingUp} label="Today" value={data.today} tone="blue" testid="kpi-today" />
          <KPI icon={Layers} label="Avg resolution" value={`${data.avg_resolution_hours || 0}h`} tone="slate" testid="kpi-avg" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="font-display text-lg">Complaints by zone</CardTitle></CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.by_zone || []} margin={{ top: 10, right: 10, left: -10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="zone_name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="resolved" stackId="a" fill="hsl(142 71% 45%)" name="Resolved" />
                  <Bar dataKey="pending" stackId="a" fill="hsl(38 92% 50%)" name="Pending" />
                  <Bar dataKey="escalated" stackId="a" fill="hsl(0 84% 60%)" name="Escalated" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">By status</CardTitle></CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                    {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="font-display text-lg">7-day trend</CardTitle></CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="hsl(221 83% 53%)" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Top noise sources</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(data.by_source || []).slice(0, 6).map((s, i) => {
                  const total = (data.by_source || []).reduce((a, b) => a + b.count, 0) || 1;
                  const pct = Math.round((s.count / total) * 100);
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium capitalize">{s.source.replace("_", " ")}</span>
                        <span className="text-muted-foreground">{s.count} · {pct}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-slate-800" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {!(data.by_source || []).length && <div className="text-sm text-muted-foreground">No data yet.</div>}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <MapIcon className="w-5 h-5" /> Live zone heatmap
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ZoneHeatmap />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Zone performance</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-4 overline text-slate-500">Zone</th>
                    <th className="py-2 px-4 overline text-slate-500 text-right">Total</th>
                    <th className="py-2 px-4 overline text-slate-500 text-right">Pending</th>
                    <th className="py-2 px-4 overline text-slate-500 text-right">Resolved</th>
                    <th className="py-2 px-4 overline text-slate-500 text-right">Escalated</th>
                    <th className="py-2 pl-4 overline text-slate-500 text-right">Resolution %</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_zone || []).map((z) => {
                    const rate = z.total ? Math.round((z.resolved / z.total) * 100) : 0;
                    return (
                      <tr key={z.zone} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-3 pr-4 font-medium">{z.zone_name}</td>
                        <td className="py-3 px-4 text-right">{z.total}</td>
                        <td className="py-3 px-4 text-right text-amber-800">{z.pending}</td>
                        <td className="py-3 px-4 text-right text-emerald-800">{z.resolved}</td>
                        <td className="py-3 px-4 text-right text-red-800">{z.escalated}</td>
                        <td className="py-3 pl-4 text-right font-semibold">{rate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

const TONE = {
  slate: "bg-slate-50 text-slate-800 border-slate-200",
  amber: "bg-amber-50 text-amber-900 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
  red: "bg-red-50 text-red-900 border-red-200",
  blue: "bg-blue-50 text-blue-900 border-blue-200",
};

function KPI({ icon: Icon, label, value, tone = "slate", testid }) {
  return (
    <div className={`p-4 rounded-lg border ${TONE[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="mt-2 font-display font-bold text-3xl tracking-tight">{value}</div>
    </div>
  );
}
